import {
  Layers,
  Mesh,
  MeshBasicMaterial,
  MeshDepthMaterial,
  NoBlending,
  OrthographicCamera,
  PlaneGeometry,
  RGBADepthPacking,
  Scene,
  ShaderMaterial,
  WebGLRenderer,
  WebGLRenderTarget,
} from 'three';
import { HorizontalBlurShader } from 'three/addons/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/addons/shaders/VerticalBlurShader.js';

/**
 * Layer index that shadow casters (tiles) opt into. Anything on this layer
 * shows up in the shadow render pass; the main camera renders the default
 * layer (0) and ignores it.
 */
export const SHADOW_CASTER_LAYER = 1;

export interface ShadowArea {
  width: number;
  depth: number;
  centerX?: number;
  centerZ?: number;
}

export interface ShadowPassOptions {
  renderer: WebGLRenderer;
  scene: Scene;
  area: ShadowArea;
  resolution?: number;
  height?: number;
  blur?: number;
  darkness?: number;
  opacity?: number;
  y?: number;
}

export interface ShadowPass {
  plane: Mesh;
  update(): void;
  setArea(area: ShadowArea): void;
  setOpacity(opacity: number): void;
  setBlur(blur: number): void;
  setDarkness(darkness: number): void;
  dispose(): void;
}

/**
 * Render-to-texture contact shadow: top-down ortho depth render of all
 * shadow casters, two-pass separable Gaussian blur, composited onto a
 * single ground plane. Mahjong stacks of 5 tiles look terrible with
 * per-tile shadow blobs — they smear into unreadable gradients. One unified
 * silhouette stays crisp and reads as "thick stack on the table."
 *
 * Pattern lifted from drei's <ContactShadows> / three.js webgl_shadow_contact.
 */
export function createShadowPass(opts: ShadowPassOptions): ShadowPass {
  const { renderer, scene } = opts;
  const resolution = opts.resolution ?? 1024;
  let blur = opts.blur ?? 2.5;
  let darkness = opts.darkness ?? 1.0;
  const planeY = opts.y ?? 0.001;

  const rt = new WebGLRenderTarget(resolution, resolution);
  rt.texture.generateMipmaps = false;
  const rtBlur = new WebGLRenderTarget(resolution, resolution);
  rtBlur.texture.generateMipmaps = false;

  const planeGeom = new PlaneGeometry(1, 1);
  const planeMat = new MeshBasicMaterial({
    map: rt.texture,
    transparent: true,
    opacity: opts.opacity ?? 0.85,
    depthWrite: false,
  });
  const plane = new Mesh(planeGeom, planeMat);
  plane.rotation.x = -Math.PI / 2;
  plane.scale.y = -1;
  plane.position.y = planeY;
  plane.renderOrder = 2;

  const cam = new OrthographicCamera();
  cam.layers = new Layers();
  cam.layers.set(SHADOW_CASTER_LAYER);

  const depthMat = new MeshDepthMaterial({
    depthPacking: RGBADepthPacking,
    blending: NoBlending,
  });
  const darknessUniform = { value: darkness };
  depthMat.userData.darknessUniform = darknessUniform;
  depthMat.onBeforeCompile = (shader) => {
    shader.uniforms.darkness = darknessUniform;
    // smoothstep density curve: tiles close to the felt cast full-strength
    // shadow; lifted tiles fade. Full strength below ~y=0.4, faded by ~y=1.6.
    shader.fragmentShader = `uniform float darkness;\n${shader.fragmentShader.replace(
      'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
      'gl_FragColor = vec4( vec3( 0.0 ), smoothstep( 0.55, 0.92, fragCoordZ ) * darkness );',
    )}`;
  };

  const blurScene = new Scene();
  const blurCam = new OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
  const horizontalBlurMat = new ShaderMaterial({ ...HorizontalBlurShader });
  horizontalBlurMat.depthTest = false;
  const verticalBlurMat = new ShaderMaterial({ ...VerticalBlurShader });
  verticalBlurMat.depthTest = false;
  const blurQuad = new Mesh(new PlaneGeometry(1, 1), horizontalBlurMat);
  blurScene.add(blurQuad);

  function setArea(area: ShadowArea) {
    const cx = area.centerX ?? 0;
    const cz = area.centerZ ?? 0;
    const halfW = area.width / 2;
    const halfD = area.depth / 2;
    const far = opts.height ?? 5;
    cam.left = -halfW;
    cam.right = halfW;
    cam.top = halfD;
    cam.bottom = -halfD;
    cam.near = 0;
    cam.far = far;
    cam.position.set(cx, far, cz);
    cam.lookAt(cx, 0, cz);
    cam.updateProjectionMatrix();
    plane.position.set(cx, planeY, cz);
    plane.scale.set(area.width, -area.depth, 1);
  }
  setArea(opts.area);

  function blurPass(amount: number) {
    blurQuad.material = horizontalBlurMat;
    horizontalBlurMat.uniforms.tDiffuse!.value = rt.texture;
    horizontalBlurMat.uniforms.h!.value = amount / resolution;
    renderer.setRenderTarget(rtBlur);
    renderer.render(blurScene, blurCam);

    blurQuad.material = verticalBlurMat;
    verticalBlurMat.uniforms.tDiffuse!.value = rtBlur.texture;
    verticalBlurMat.uniforms.v!.value = amount / resolution;
    renderer.setRenderTarget(rt);
    renderer.render(blurScene, blurCam);
  }

  function update() {
    const prevOverride = scene.overrideMaterial;
    const prevBackground = scene.background;
    const prevAutoClear = renderer.autoClear;
    const prevTarget = renderer.getRenderTarget();

    const planeWasVisible = plane.visible;
    plane.visible = false;

    scene.overrideMaterial = depthMat;
    scene.background = null;
    darknessUniform.value = darkness;

    renderer.autoClear = true;
    renderer.setRenderTarget(rt);
    renderer.setClearColor(0x000000, 0);
    renderer.clear();
    renderer.render(scene, cam);

    scene.overrideMaterial = prevOverride;

    blurPass(blur);
    blurPass(blur * 0.4);

    scene.background = prevBackground;
    renderer.autoClear = prevAutoClear;
    renderer.setRenderTarget(prevTarget);
    plane.visible = planeWasVisible;
  }

  return {
    plane,
    update,
    setArea,
    setOpacity(opacity) {
      planeMat.opacity = opacity;
    },
    setBlur(b) {
      blur = b;
    },
    setDarkness(d) {
      darkness = d;
    },
    dispose() {
      rt.dispose();
      rtBlur.dispose();
      planeGeom.dispose();
      planeMat.dispose();
      depthMat.dispose();
      horizontalBlurMat.dispose();
      verticalBlurMat.dispose();
      blurQuad.geometry.dispose();
    },
  };
}
